import React, { useMemo } from 'react';
import {
  HStack, VStack, Box, Text, Pressable, Tooltip, TooltipContent, TooltipText,
} from '@gluestack-ui/themed';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useEvaluationGradingConfig } from '@/lecturer/EvaluationDesk/context/EvaluationGradingConfigContext';

export interface DimRatingProps {
  dimension: string;
  evalFieldName: string;
  isAutograding: boolean;
  isSaving: boolean;
  isReadOnly: boolean;
  /**
   * Layout mode for the row.
   * - 'inline' (default): compact, left-aligned, single-line with sibling dims.
   * - 'row': full-width row with the label above the dots so ladders align
   *   vertically when stacked in a list.
   */
  layout?: 'inline' | 'row';
}

/**
 * Single dimension with horizontal dot rating.
 */
const DimRating: React.FC<DimRatingProps> = ({
  dimension, evalFieldName, isAutograding, isSaving, isReadOnly, layout = 'inline',
}) => {
  const { t } = useTranslation();
  const { performanceLevels, dimensionColors, evalDimensions } = useEvaluationGradingConfig();
  const { setValue, control } = useFormContext();

  // Do not pass defaultValue: RHF then ignores form defaultValues until the
  // leaf is registered (setValue on click) — Harvey balls stay empty while
  // comments (RTE) still show the payload.
  const currentLevel = useWatch({
    control,
    name: `${evalFieldName}.${dimension}.level`,
  });

  const dimensionColor = dimensionColors[dimension] || '$textSubtle';
  const dimensionLabel = evalDimensions?.[dimension] || dimension;
  const dimensionDescription = t(`lecturer.evalDesk.gradingDimHints.${dimension}`, { defaultValue: dimension });

  const ratingLevels = useMemo(
    () => performanceLevels
      .filter((level) => level.id !== 'n/a' && level.id !== 'not_applicable')
      .sort((a, b) => (a.performancePercent || 0) - (b.performancePercent || 0)),
    [performanceLevels],
  );

  const handleDotClick = (levelId: string) => {
    if (isReadOnly || isAutograding || isSaving) return;
    if (currentLevel === levelId) {
      setValue(`${evalFieldName}.${dimension}.level`, null, { shouldValidate: true, shouldDirty: true });
    } else {
      setValue(`${evalFieldName}.${dimension}.level`, levelId, { shouldValidate: true, shouldDirty: true });
    }
  };

  const getFilledCount = () => {
    if (!currentLevel || currentLevel === 'not_applicable') return 0;
    const levelIndex = ratingLevels.findIndex((l) => l.id === currentLevel);
    if (levelIndex === -1) return 0;
    return levelIndex + 1;
  };

  const filledCount = getFilledCount();

  // Keep the current-level probe out of flex flow. opacity:0 + height:0 still
  // consumes the string's width in an HStack, which made four dims wrap (or
  // paint under the sticky right pane when nowrap).
  const levelProbe = (
    <Text
      testID={`eval-dim-${dimension}-current`}
      position="absolute"
      w={0}
      h={0}
      overflow="hidden"
      opacity={0}
    >
      {currentLevel ?? 'unset'}
    </Text>
  );

  const dots = (
    <HStack space="xs" alignItems="center">
      {ratingLevels.map((level, index) => {
        const isFilled = index < filledCount;
        return (
          <Pressable
            key={level.id}
            onPress={() => handleDotClick(level.id)}
            disabled={isReadOnly || isAutograding || isSaving}
            p="$0.5"
            opacity={isReadOnly || isAutograding || isSaving ? 0.5 : 1}
            testID={`eval-dim-${dimension}-level-${index + 1}`}
          >
            <Box
              w={10}
              h={10}
              borderRadius="$full"
              bg={isFilled ? dimensionColor : '$backgroundLight300'}
              opacity={isFilled ? 1 : 0.4}
            />
          </Pressable>
        );
      })}
    </HStack>
  );

  return (
    <Tooltip
      placement="top"
      openDelay={200}
      trigger={(triggerProps) => (layout === 'row' ? (
        // Stacked: tiny caption-style label sits above its dot ladder.
        // Repetition across questions reads as a quiet section caption,
        // not a redundant form label.
        <VStack
          {...triggerProps}
          space="xs"
          w="100%"
          position="relative"
          testID={`eval-dim-${dimension}`}
          accessibilityValue={{ text: currentLevel ?? 'unset' }}
        >
          {levelProbe}
          <Text
            size="2xs"
            fontWeight="medium"
            color={dimensionColor}
            textTransform="uppercase"
            letterSpacing={0.5}
            numberOfLines={1}
          >
            {dimension}
          </Text>
          {dots}
        </VStack>
      ) : (
        <HStack
          {...triggerProps}
          alignItems="center"
          space="xs"
          flexGrow={0}
          flexShrink={0}
          alignSelf="flex-start"
          position="relative"
          testID={`eval-dim-${dimension}`}
          accessibilityValue={{ text: currentLevel ?? 'unset' }}
        >
          {levelProbe}
          <Text
            size="2xs"
            fontWeight="semibold"
            color={dimensionColor}
            textTransform="uppercase"
            letterSpacing={0}
            numberOfLines={1}
          >
            {dimension}
          </Text>
          {dots}
        </HStack>
      ))}
    >
      <TooltipContent maxWidth={240} bg="$backgroundDark800" p="$2">
        <TooltipText>
          <Text color="$white" size="xs" fontWeight="semibold">
            {dimensionLabel}
          </Text>
          {'\n'}
          <Text color="$textHint" size="xs">
            {dimensionDescription}
          </Text>
        </TooltipText>
      </TooltipContent>
    </Tooltip>
  );
};

export default DimRating;
